<template>
  <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`" class="medal-progress-ring">
    <!-- Background track -->
    <circle
      :cx="center"
      :cy="center"
      :r="radius"
      fill="none"
      :stroke="trackColor"
      :stroke-width="strokeWidth"
    />
    <!-- Progress arc -->
    <circle
      :cx="center"
      :cy="center"
      :r="radius"
      fill="none"
      :stroke="color"
      :stroke-width="strokeWidth"
      stroke-linecap="round"
      :stroke-dasharray="circumference"
      :stroke-dashoffset="animatedOffset"
      :transform="`rotate(-90 ${center} ${center})`"
      class="medal-progress-ring__arc"
    />
    <!-- Center text: percentage or count -->
    <text
      :x="center"
      :y="center - 6"
      text-anchor="middle"
      dominant-baseline="middle"
      class="medal-progress-ring__pct"
      :fill="color"
    >{{ animatedPct }}%</text>
    <text
      :x="center"
      :y="center + 10"
      text-anchor="middle"
      dominant-baseline="middle"
      class="medal-progress-ring__count"
      :fill="countColor"
    >{{ fmtNum(animatedCount) }}/{{ fmtNum(nextThreshold) }}</text>
  </svg>
</template>

<script>
export default {
  name: 'MedalProgressRing',
  props: {
    percentage:    { type: Number, default: 0 },
    currentCount:  { type: Number, default: 0 },
    nextThreshold: { type: Number, default: null },
    size:          { type: Number, default: 110 },
    strokeWidth:   { type: Number, default: 8 },
    color:         { type: String, default: '#FFD700' },
    darkMode:      { type: Boolean, default: false },
  },
  data() {
    return {
      animatedPct:    0,
      animatedCount:  0,
      animatedOffset: 0,
    }
  },
  computed: {
    center()       { return this.size / 2 },
    radius()       { return (this.size - this.strokeWidth * 2) / 2 },
    circumference(){ return 2 * Math.PI * this.radius },
    targetOffset() {
      return this.circumference - (this.percentage / 100) * this.circumference
    },
    trackColor()   { return this.darkMode ? '#2d3748' : '#dde1e7' },
    countColor()   { return this.darkMode ? '#a0aec0' : '#8a96a8' },
  },
  mounted() {
    this.$nextTick(() => this.animate())
  },
  watch: {
    percentage() { this.animate() },
  },
  methods: {
    animate() {
      const duration   = 1200
      const startTime  = performance.now()
      const startPct   = this.animatedPct
      const startCount = this.animatedCount
      const startOffset = this.circumference

      const step = (now) => {
        const elapsed  = Math.min(now - startTime, duration)
        const progress = this.easeOut(elapsed / duration)

        this.animatedPct    = Math.round(startPct + (this.percentage - startPct) * progress)
        this.animatedCount  = Math.round(startCount + (this.currentCount - startCount) * progress)
        this.animatedOffset = startOffset + (this.targetOffset - startOffset) * progress

        if (elapsed < duration) {
          requestAnimationFrame(step)
        } else {
          this.animatedPct    = this.percentage
          this.animatedCount  = this.currentCount
          this.animatedOffset = this.targetOffset
        }
      }

      requestAnimationFrame(step)
    },
    easeOut(t) { return 1 - Math.pow(1 - t, 3) },
    fmtNum(n) {
      if (n == null) return ''
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
      return String(Math.round(n))
    },
  },
}
</script>

<style scoped>
.medal-progress-ring {
  display: block;
}
.medal-progress-ring__arc {
  transition: stroke 0.3s;
}
.medal-progress-ring__pct {
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
}
.medal-progress-ring__count {
  font-size: 10px;
  font-family: inherit;
}
</style>
